'use client'

import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import HotelRoundedIcon from '@mui/icons-material/HotelRounded'
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import { alpha, Box, IconButton, Stack, Typography } from '@mui/material'
import React from 'react'

import FlightIcon from '@/src/assets/svgs/FlightIcon'
import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer/swipeableDrawer'
import { TripTitleParts } from '@/src/utils/trips/tripListHelpers'

interface UpcomingTripNudgeProps {
    title: string
    tripTitleParts: TripTitleParts
    tripDateLabel: string
    tripIconType: 'flight' | 'hotel'
    showCheckAll?: boolean
    checkAllLabel?: string
    onCheckAllClick?: () => void
    onNavigate: () => void
    onDismiss: () => void
}

interface UpcomingTripsDrawerRow {
    id: string
    titleParts: TripTitleParts
    dateLabel: string
    iconType: 'flight' | 'hotel'
    tripType?: string
    bookingSource?: 'MAIN' | 'LINKED'
}

interface UpcomingTripsDrawerProps {
    open: boolean
    title: string
    viewAllLabel: string
    rows: UpcomingTripsDrawerRow[]
    onClose: () => void
    onViewAll: () => void
    onRowClick: (tripId: string, tripType?: string, bookingSource?: 'MAIN' | 'LINKED') => void
}

const UpcomingTripNudge: React.FC<UpcomingTripNudgeProps> = ({
    title,
    tripTitleParts,
    tripDateLabel,
    tripIconType,
    showCheckAll = false,
    checkAllLabel = '',
    onCheckAllClick,
    onNavigate,
    onDismiss
}) => {
    const NudgeTripIcon = tripIconType === 'hotel' ? HotelRoundedIcon : FlightIcon

    return (
        <Box
            onClick={onNavigate}
            sx={{
                position: 'fixed',
                bottom: 56,
                left: 0,
                right: 0,
                zIndex: 999,
                px: 1,
                mb: 3,
                cursor: 'pointer'
            }}
        >
            {showCheckAll && checkAllLabel && (
                <Box
                    onClick={event => {
                        event.stopPropagation()
                        onCheckAllClick?.()
                    }}
                    sx={{
                        position: 'absolute',
                        left: '50%',
                        top: -18,
                        transform: 'translateX(-50%)',
                        bgcolor: '#22314A',
                        color: 'common.white',
                        border: '1px solid',
                        borderColor: theme => alpha(theme.palette.common.white, 0.22),
                        borderRadius: '12px',
                        px: 1.75,
                        py: 0.5,
                        fontSize: 14,
                        fontWeight: 700,
                        lineHeight: 1.1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25
                    }}
                >
                    {checkAllLabel}
                    <ExpandLessIcon sx={{ fontSize: 18, color: 'inherit' }} />
                </Box>
            )}
            <Box
                sx={{
                    borderRadius: '12px',
                    bgcolor: '#1E2939',
                    color: 'common.white',
                    px: '16px',
                    py: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '64px'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '12px',
                            bgcolor: alpha('#00D186', 0.08),
                            backdropFilter: 'blur(6px)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}
                    >
                        <NudgeTripIcon
                            sx={{
                                color: 'success.main',
                                fontSize: 16
                            }}
                        />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontSize: '0.625rem',
                                fontWeight: 700,
                                letterSpacing: '0.08em',
                                lineHeight: 1.2,
                                color: 'success.main',
                                textTransform: 'uppercase'
                            }}
                        >
                            {title}
                        </Typography>
                        <Typography
                            sx={{
                                mt: 0.4,
                                fontSize: 14,
                                fontWeight: 500,
                                lineHeight: 1.25,
                                color: 'common.white',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                        >
                            {tripTitleParts.isRoundTrip && tripTitleParts.origin && tripTitleParts.destination ? (
                                <>
                                    {tripTitleParts.origin}
                                    <SwapHorizIcon sx={{ fontSize: 16, mx: 0.5, verticalAlign: 'text-bottom' }} />
                                    {tripTitleParts.destination}
                                    {!!tripDateLabel && ` \u2022 ${tripDateLabel}`}
                                </>
                            ) : (
                                `${tripTitleParts.fullTitle}${tripDateLabel ? ` \u2022 ${tripDateLabel}` : ''}`
                            )}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <KeyboardArrowRightRoundedIcon sx={{ fontSize: 34, color: 'common.white' }} />
                    <Box
                        sx={{
                            mx: 1,
                            height: 32,
                            borderLeft: '1px solid',
                            borderColor: theme => alpha(theme.palette.common.white, 0.25)
                        }}
                    />
                    <IconButton
                        size="small"
                        onClick={event => {
                            event.stopPropagation()
                            onDismiss()
                        }}
                        sx={{
                            color: 'common.white'
                        }}
                    >
                        <CloseRoundedIcon />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    )
}

const UpcomingTripsDrawer: React.FC<UpcomingTripsDrawerProps> = ({
    open,
    title,
    viewAllLabel,
    rows,
    onClose,
    onViewAll,
    onRowClick
}) => {
    return (
        <SwipeableDrawerWithBackNavigation
            open={open}
            onClose={onClose}
            disableSwipeToOpen
            sx={{ height: '72dvh', maxHeight: '72dvh', bgcolor: '#F5F6F8' }}
        >
            <Box
                sx={{
                    px: 3,
                    pt: 2,
                    pb: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    position: 'relative'
                }}
            >
                <Typography
                    sx={{
                        fontSize: 18,
                        fontWeight: 700,
                        lineHeight: 1.2,
                        color: 'text.primary',
                        textAlign: 'center'
                    }}
                >
                    {title}
                </Typography>
                <Box
                    onClick={onViewAll}
                    sx={{
                        position: 'absolute',
                        right: 24,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'primary.main',
                        fontSize: 16,
                        fontWeight: 600,
                        lineHeight: 1.25,
                        cursor: 'pointer'
                    }}
                >
                    {viewAllLabel}
                </Box>
            </Box>

            <Box sx={{ px: 2, py: 2, overflowY: 'auto', bgcolor: '#F5F6F8' }}>
                <Stack spacing={1.5}>
                    {rows.map(row => {
                        const RowIcon = row.iconType === 'hotel' ? HotelRoundedIcon : FlightIcon

                        return (
                            <Box
                                key={row.id}
                                onClick={() => {
                                    onRowClick(row.id, row.tripType, row.bookingSource)
                                    onClose()
                                }}
                                sx={{
                                    borderRadius: '20px',
                                    border: '1px solid',
                                    borderColor: '#D4D8DE',
                                    bgcolor: 'common.white',
                                    px: 2.5,
                                    py: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1.5,
                                    cursor: 'pointer'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                                    <Box
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: '12px',
                                            bgcolor: '#EEF1F7',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <RowIcon
                                            sx={{
                                                color: '#1D4F97',
                                                fontSize: 16
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                fontSize: 14,
                                                fontWeight: 600,
                                                lineHeight: 1.15,
                                                color: '#24262B'
                                            }}
                                        >
                                            {row.titleParts.isRoundTrip && row.titleParts.origin && row.titleParts.destination ? (
                                                <>
                                                    <Box component="span">{row.titleParts.origin}</Box>
                                                    <SwapHorizIcon sx={{ fontSize: 16, mx: 0.5, verticalAlign: 'text-bottom' }} />
                                                    <Box component="span">{row.titleParts.destination}</Box>
                                                </>
                                            ) : (
                                                row.titleParts.fullTitle
                                            )}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                mt: 0.5,
                                                fontSize: 12,
                                                fontWeight: 500,
                                                lineHeight: 1.3,
                                                color: '#646B7A'
                                            }}
                                        >
                                            {row.dateLabel}
                                        </Typography>
                                    </Box>
                                </Box>

                                <KeyboardArrowRightRoundedIcon sx={{ fontSize: 34, color: '#6E7787', flexShrink: 0 }} />
                            </Box>
                        )
                    })}
                </Stack>
            </Box>
        </SwipeableDrawerWithBackNavigation>
    )
}

export { UpcomingTripNudge }
export { UpcomingTripsDrawer }
export type { UpcomingTripNudgeProps, UpcomingTripsDrawerProps, UpcomingTripsDrawerRow }
